import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import {
  X, User, Save, Trash2, Search,
  Briefcase, Clock, Upload, Eye, PlusCircle,
  GripVertical, Layers, Edit2, Plus, ChevronRight, ChevronLeft, FileText,
  MapPin, Calendar as CalendarIcon, Users, Info, Scissors, Camera,
  CheckCircle, AlertCircle, Map as MapIcon, Route
} from 'lucide-react';
import { toast } from 'react-toastify';
import { format, parseISO, isSameDay } from 'date-fns';

// Utilities & Components
import { calcBusinessHours, addBusinessHours, getTimelineSegments, fetchTravelInfo } from '../../utils/planningUtils';
import RangeDatePicker from '../../components/admin/RangeDatePicker';
import TravelIndicator from '../../components/admin/TravelIndicator';
import AddressAutocomplete from '../../components/dashboard/AddressAutocomplete';

// Styles
import styles from '../Dashboard.module.css';
import planningStyles from './Planning.module.css';
import '../../styles/CalendarOverrides.css';

// --- Constants & Helper Components ---
const WORKER_COLORS = [
  '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#f43f5e', '#6366f1', '#14b8a6', '#f97316', '#2dd4bf'
];

function getWorkerColor(user) {
  if (!user) return '#cbd5e1';
  if (user.color) return user.color;
  const id = user.id || '';
  let hash = 0;
  for (let i = 0; i < id.length; i++) { hash = id.charCodeAt(i) + ((hash << 5) - hash); }
  return WORKER_COLORS[Math.abs(hash) % WORKER_COLORS.length];
}

// --- Planning Component ---
const Planning = () => {
  const { chantiers: rawChantiers, users: allUsers, refreshData } = useOutletContext();
  const [interventions, setInterventions] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('dayGridMonth');
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCutterMode, setIsCutterMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) setShowSidebar(true);
      if (mobile && viewType === 'dayGridMonth') {
        setViewType('timeGridDay'); // Force day view on mobile to save space
      }
    };
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewType]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState({
    id: '', title: '', project_id: '', assigned_staff: [],
    start: '', end: '', status: 'planned', description: '',
    address: '', type: 'mission', hours_override: '', notes: ''
  });

  // Delete Confirmation State
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: '', name: '', type: 'chantier' });

  const calendarRef = useRef(null);
  const sidebarRef = useRef(null);
  const draggableInstance = useRef(null);

  // --- Data Fetching ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [interRecords, settingsList, timesheetRecords] = await Promise.all([
        pb.collection('interventions').getFullList({ expand: 'project_id,assigned_staff' }),
        pb.collection('settings').getFullList(),
        pb.collection('timesheets').getFullList()
      ]);
      setInterventions(interRecords);
      setTimesheets(timesheetRecords);
      setSettings(settingsList[0] || null);
    } catch (err) {
      console.error("Error fetching planning data:", err);
      toast.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const unsub = pb.collection('interventions').subscribe('*', fetchData);
    return () => unsub.then(f => f());
  }, [fetchData]);

  // --- External Draggable Setup ---
  useEffect(() => {
    if (sidebarRef.current) {
      if (draggableInstance.current) {
        draggableInstance.current.destroy();
      }
      draggableInstance.current = new Draggable(sidebarRef.current, {
        itemSelector: '.fc-external-event',
        eventData: function(eventEl) {
          return {
            title: eventEl.getAttribute('data-title'),
            duration: '08:00',
            extendedProps: {
              project_id: eventEl.getAttribute('data-id'),
              type: 'mission'
            }
          };
        }
      });
    }
    return () => {
      if (draggableInstance.current) {
        draggableInstance.current.destroy();
        draggableInstance.current = null;
      }
    };
  }, [rawChantiers, loading]);

  const businessHours = settings?.business_hours || {
    monday: { active: true, open: '08:00', close: '17:30' },
    tuesday: { active: true, open: '08:00', close: '17:30' },
    wednesday: { active: true, open: '08:00', close: '17:30' },
    thursday: { active: true, open: '08:00', close: '17:30' },
    friday: { active: true, open: '08:00', close: '17:00' },
    saturday: { active: false, open: '08:00', close: '12:00' },
    sunday: { active: false, open: '08:00', close: '12:00' }
  };
  const closures = settings?.exceptional_closures || [];

  // --- Event Generation ---
  const events = useMemo(() => {
    const filtered = selectedWorkerIds.length === 0
      ? interventions
      : interventions.filter(i => {
          const staff = Array.isArray(i.assigned_staff) ? i.assigned_staff : (i.assigned_staff ? [i.assigned_staff] : []);
          return staff.some(uid => selectedWorkerIds.includes(uid));
      });

    const groups = {};
    const unified = [];
    filtered.forEach(i => {
      const type = i.type || 'mission';
      const key = type === 'rdv' ? `rdv-${i.title}-${i.start}-${i.end}` : `mission-${i.project_id}-${i.start}-${i.end}`;
      if (!groups[key]) {
        groups[key] = { 
          id: i.id, key, project_id: i.project_id, 
          start: i.start, end: i.end, title: i.title, type, 
          interventions: [], chantier: i.expand?.project_id 
        };
        unified.push(groups[key]);
      }
      groups[key].interventions.push(i);
    });

    const finalEvents = [];
    unified.forEach(g => {
      try {
        const startStr = (g.start || '').replace(' ', 'T');
        let endStr = (g.end || '').replace(' ', 'T');
        const start = new Date(startStr);
        if (isNaN(start.getTime())) return;

        let end = new Date(endStr);
        if (isNaN(end.getTime()) || end <= start) {
          end = new Date(start.getTime() + 60 * 60 * 1000);
          endStr = end.toISOString();
        }

        finalEvents.push({
          id: g.id,
          groupId: g.key,
          title: g.title || g.chantier?.name || 'Chantier',
          start: startStr,
          end: endStr,
          color: g.chantier?.color || (g.type === 'rdv' ? '#a855f7' : '#3b82f6'),
          display: 'block',
          extendedProps: {
            active: true,
            type: g.type,
            chantier: g.chantier,
            users: g.interventions.flatMap(i => i.expand?.assigned_staff || []).filter(Boolean),
            raw: g.interventions[0],
            rawIds: g.interventions.map(i => i.id),
            durationHours: calcBusinessHours(startStr, endStr, businessHours, closures) || 8
          }
        });
      } catch (e) {
        console.error("Error processing group", g.key, e);
      }
    });
    return finalEvents;
  }, [interventions, selectedWorkerIds]);

  // --- Sidebar Chantiers with Stats ---
  const sidebarChantiers = useMemo(() => {
    if (!rawChantiers) return [];
    const result = rawChantiers
      .filter(c => {
        const isNotAtelier = c.name?.toLowerCase() !== 'atelier' && c.name?.toLowerCase() !== 'atelier / dépôt';
        const matchesStatus = c.status !== 'completed' && c.status !== 'TERMINE' && c.status !== 'ANNULE';
        const matchesSearch = !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch && isNotAtelier;
      })
      .map(c => {
        const chantierInters = interventions.filter(i => i.project_id === c.id);
        const consumed = chantierInters.reduce((acc, i) => acc + (parseFloat(i.hours_override) || 8), 0);
        
        const chantierTimesheets = timesheets.filter(t => t.chantier === c.id);
        const real_hours = chantierTimesheets.reduce((acc, t) => acc + (t.hours || 0), 0);
        
        return { ...c, consumed_hours: consumed, real_hours: real_hours };
      });
    return result;
  }, [rawChantiers, interventions, timesheets, searchTerm]);

  // --- Handlers ---
  const handleConfirmDelete = async () => {
    const { id, type } = deleteConfirm;
    try {
      if (type === 'chantier') {
        await pb.collection('chantiers').delete(id);
        toast.success("Chantier supprimé avec succès");
        if (refreshData) refreshData();
      } else {
        await pb.collection('interventions').delete(id);
        toast.success("Intervention supprimée");
        setIsModalOpen(false);
      }
      setDeleteConfirm({ open: false, id: '', name: '', type: 'chantier' });
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEventClick = (info) => {
    const raw = info.event.extendedProps.raw;
    const users = info.event.extendedProps.users || [];
    if (!raw) return;
    setModalMode('edit');
    setFormData({
      id: raw.id,
      title: raw.title || '',
      project_id: raw.project_id || '',
      assigned_staff: users.map(u => u.id),
      start: raw.start,
      end: raw.end,
      status: raw.status || 'planned',
      description: raw.description || '',
      address: raw.address || '',
      type: raw.type || 'mission',
      hours_override: raw.hours_override || '',
      notes: raw.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleEventReceive = (info) => {
    const { event, view } = info;
    
    let startDate = new Date(event.start);
    if (view.type === 'dayGridMonth' || (startDate.getHours() === 0 && startDate.getMinutes() === 0)) {
      startDate.setHours(8, 0, 0, 0);
    }
    
    const start = startDate.toISOString();
    const end = addBusinessHours(start, 8, businessHours, closures);
    const staffToAssign = selectedWorkerIds.length > 0 ? selectedWorkerIds : [];

    // On pré-remplit le formulaire et on ouvre la modale
    setFormData({
      id: '',
      title: '',
      project_id: event.extendedProps.project_id,
      assigned_staff: staffToAssign,
      start,
      end,
      status: 'planned',
      description: '',
      address: '',
      type: 'mission',
      hours_override: '',
      notes: ''
    });
    setModalMode('create');
    setIsModalOpen(true);
    
    // On retire l'événement temporaire du calendrier
    info.revert();
  };

  const handleEventDrop = async (info) => {
    const { event, view, oldEvent } = info;
    const rawIds = event.extendedProps.rawIds;
    if (!rawIds?.length) return;
    try {
      // Use the pre-calculated duration stored in extendedProps
      const originalHours = event.extendedProps.durationHours || 8;
      
      let startDate = new Date(event.start);
      
      // Si on déplace sur la vue mois, l'heure est réinitialisée à 00:00 par FullCalendar.
      // On restaure l'ancienne heure si possible, sinon on met 08:00 par défaut.
      if (view.type === 'dayGridMonth') {
        if (oldEvent && oldEvent.start) {
          startDate.setHours(oldEvent.start.getHours(), oldEvent.start.getMinutes(), 0, 0);
        } else {
          startDate.setHours(8, 0, 0, 0);
        }
      } else if (startDate.getHours() === 0 && startDate.getMinutes() === 0 && event.allDay) {
        startDate.setHours(8, 0, 0, 0);
      }
      
      const newStart = startDate.toISOString();
      const newEnd = addBusinessHours(newStart, originalHours, businessHours, closures);
      
      await Promise.all(rawIds.map(id => pb.collection('interventions').update(id, { start: newStart, end: newEnd })));
      toast.success(`Déplacé : ${originalHours}h conservées`);
      fetchData();
    } catch (e) { 
      info.revert(); 
      toast.error("Erreur de déplacement"); 
    }
  };

  const handleEventResize = async (info) => {
    const { event } = info;
    const rawIds = event.extendedProps.rawIds;
    if (!rawIds?.length) return;
    try {
      const newHours = calcBusinessHours(event.start, event.end, businessHours, closures);
      await Promise.all(rawIds.map(id => pb.collection('interventions').update(id, { start: event.start.toISOString(), end: event.end.toISOString(), hours_override: newHours })));
      toast.success("Durée mise à jour");
    } catch (e) { info.revert(); toast.error("Erreur redimensionnement"); }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      // Nettoyage des données avant envoi
      const dataToSave = { 
        ...formData, 
        start: new Date(formData.start).toISOString(), 
        end: new Date(formData.end).toISOString() 
      };
      
      // PocketBase n'aime pas les chaînes vides pour les nombres
      if (dataToSave.hours_override === '') {
        dataToSave.hours_override = null;
      }
      // PocketBase n'aime pas les chaînes vides pour les relations
      if (dataToSave.project_id === '') {
        dataToSave.project_id = null;
      }

      if (modalMode === 'edit') {
        await pb.collection('interventions').update(formData.id, dataToSave);
        toast.success("Intervention mise à jour");
      } else {
        await pb.collection('interventions').create(dataToSave);
        toast.success("Nouvelle intervention créée");
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) { 
      console.error("Save Error:", err);
      toast.error("Erreur de sauvegarde: " + (err?.data?.message || err.message)); 
    } finally { setLoading(false); }
  };

  const handleDelete = () => {
    setDeleteConfirm({ 
      open: true, 
      id: formData.id, 
      name: formData.title || selectedChantier?.name || "cette intervention", 
      type: 'intervention' 
    });
  };

  const handleSplit = async (event) => {
    try {
      const raw = event.extendedProps.raw;
      const midDate = new Date(event.start.getTime() + (event.end.getTime() - event.start.getTime()) / 2).toISOString();
      const h1 = calcBusinessHours(event.start, midDate, businessHours, closures);
      const h2 = calcBusinessHours(midDate, event.end, businessHours, closures);
      await pb.collection('interventions').update(raw.id, { end: midDate, hours_override: h1 });
      await pb.collection('interventions').create({ ...raw, id: undefined, start: midDate, hours_override: h2 });
      fetchData();
      toast.success("Intervention divisée");
    } catch (e) { toast.error("Erreur division"); }
  };

  // --- Rendering Helpers ---
  const renderEventContent = (info) => {
    const { event } = info;
    const type = event.extendedProps.type;
    const isRDV = type === 'rdv';
    const users = event.extendedProps.users || [];
    const color = isRDV ? '#a855f7' : (event.extendedProps.chantier?.color || '#3b82f6');
    const h = event.extendedProps.durationHours || 8;
    const d = +(h / 8).toFixed(1);
    const durationText = h >= 8 ? `${d}j (${h}h)` : `${h}h`;

    return (
      <div className={`unified-event-card ${isRDV ? 'is-rdv' : ''}`} style={{
        background: color, color: '#fff', padding: '4px 8px', borderRadius: 8, height: '100%', overflow: 'hidden',
        borderLeft: `4px solid rgba(0,0,0,0.1)`, fontSize: 11, position: 'relative'
      }}>
        <div style={{ fontWeight: 900, marginBottom: 2 }}>{event.title}</div>
        {!isRDV && (
          <div style={{ fontSize: 9, opacity: 0.9 }}>
            {users.length}p • {durationText} {event.start && (event.start.getHours() !== 0 || event.start.getMinutes() !== 0) ? `• ${format(event.start, 'HH:mm')}` : ''}
          </div>
        )}
        <div className="worker-dots" style={{ display: 'flex', gap: 2, marginTop: 4 }}>
          {users.map(u => <div key={u.id} style={{ width: 6, height: 6, borderRadius: '50%', background: getWorkerColor(u) }} title={u.name} />)}
        </div>
      </div>
    );
  };

  const selectedChantier = useMemo(() => rawChantiers?.find(c => c.id === formData.project_id), [rawChantiers, formData.project_id]);

  return (
    <div style={{ padding: 0, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '20px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-light-gray)', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Planning & Équipes</h2>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-light)' }}>Gérez les interventions et tournées en temps réel.</p>
        </div>
        <div className={planningStyles.headerControls}>
          <button className={`${styles.toggleBtn} ${isCutterMode ? 'active' : ''}`} onClick={() => setIsCutterMode(!isCutterMode)} style={{ background: isCutterMode ? '#fee2e2' : 'var(--color-light-gray)', color: isCutterMode ? '#ef4444' : 'var(--color-text-light)' }}>
            <Scissors size={16} /> {isCutterMode ? "Mode Découpe ACTIF" : "Mode Découpe"}
          </button>
          <button className={styles.addButton} onClick={() => { setModalMode('create'); setFormData({ id: '', title: '', project_id: '', assigned_staff: [], start: new Date().toISOString(), end: '', status: 'planned', description: '', address: '', type: 'mission', hours_override: '', notes: '' }); setIsModalOpen(true); }}>
            <Plus size={16} /> Nouvelle Intervention
          </button>
          {isMobile && (
            <button className={styles.viewBtn} onClick={() => setShowSidebar(!showSidebar)} style={{ background: 'var(--color-light-gray)', fontWeight: 800 }}>
              <Layers size={16} style={{ marginRight: '6px' }} /> 
              {showSidebar ? 'Masquer Outils' : 'Outils & Filtres'}
            </button>
          )}
        </div>
      </header>

      <div className={planningStyles.planningLayout}>
        {(!isMobile || showSidebar) && (
          <aside className={planningStyles.planningSidebar}>
            <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--color-surface)', padding: '10px 15px', borderRadius: 12, border: '1px solid var(--color-light-gray)' }}>
              <Search size={16} color="#94a3b8" />
              <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ border: 'none', fontSize: 13, width: '100%', outline: 'none', background: 'transparent', color: 'var(--color-text)' }} />
            </div>
          </div>

          <div style={{ marginBottom: 25 }}>
            <h4 style={{ fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={14} /> Équipes & Salariés
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allUsers?.filter(u => u.role === 'worker' || u.role === 'foreman').map(u => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', background: selectedWorkerIds.includes(u.id) ? 'var(--color-surface)' : 'transparent', borderRadius: 10, border: selectedWorkerIds.includes(u.id) ? '1px solid var(--color-light-gray)' : '1px solid transparent' }}>
                  <input type="checkbox" checked={selectedWorkerIds.includes(u.id)} onChange={() => setSelectedWorkerIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])} style={{ display: 'none' }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: getWorkerColor(u) }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>{u.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Briefcase size={14} /> Chantiers Actifs
            </h4>
            <div ref={sidebarRef} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sidebarChantiers.map(c => (
                <div key={c.id} className="fc-external-event" data-id={c.id} data-title={c.name} style={{
                  padding: '12px 15px', background: 'var(--color-surface)', border: '1px solid var(--color-light-gray)', borderRadius: 12, cursor: 'grab',
                  display: 'flex', flexDirection: 'column', gap: 6, transition: 'all 0.2s', position: 'relative',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <GripVertical size={14} color="#cbd5e1" />
                      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--color-text)' }}>{c.name}</span>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ open: true, id: c.id, name: c.name, type: 'chantier' });
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: '4px', borderRadius: '6px', transition: 'all 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#cbd5e1'}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  
                  {(c.consumed_hours > 0 || c.real_hours > 0 || c.budget_hours > 0) && (
                    <div style={{ width: '100%', marginTop: 6, background: '#f8fafc', padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-light)', marginBottom: 2 }}>
                        <span>Prévu</span>
                        <span style={{ fontWeight: 800, color: '#3b82f6' }}>{c.consumed_hours}h</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-light)', marginBottom: c.budget_hours > 0 ? 6 : 0 }}>
                        <span>Réel (Effectué)</span>
                        <span style={{ fontWeight: 800, color: c.real_hours > c.consumed_hours ? '#ef4444' : '#10b981' }}>{c.real_hours}h</span>
                      </div>
                      {c.budget_hours > 0 && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontWeight: 800, color: c.real_hours > c.budget_hours ? '#ef4444' : 'var(--color-text-light)', marginBottom: 4 }}>
                            <span>Budget Alloué</span>
                            <span>{Math.round((c.real_hours / c.budget_hours) * 100)}% ({c.budget_hours}h)</span>
                          </div>
                          <div style={{ height: 4, background: 'var(--color-light-gray)', borderRadius: 10, overflow: 'hidden' }}>
                            <div style={{ 
                              width: `${Math.min(100, (c.real_hours / c.budget_hours) * 100)}%`, 
                              height: '100%', 
                              background: c.real_hours > c.budget_hours ? '#ef4444' : '#10b981',
                              borderRadius: 10,
                              transition: 'width 0.4s ease'
                            }} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {sidebarChantiers.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', fontSize: 12, color: 'var(--color-text-light)', background: 'var(--color-light-background)', borderRadius: 12, border: '1px dashed var(--color-light-gray)' }}>
                  Aucun chantier actif trouvé
                </div>
              )}
            </div>
          </div>
        </aside>
        )}

        <main className={planningStyles.planningMain}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={viewType}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: isMobile ? 'timeGridDay,timeGridWeek' : 'dayGridMonth,timeGridWeek,timeGridDay' }}
            locale={frLocale}
            events={events}
            editable={!isCutterMode}
            droppable={true}
            selectable={true}
            eventClick={isCutterMode ? (info) => handleSplit(info.event) : handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventReceive={handleEventReceive}
            eventContent={renderEventContent}
            height="100%"
            slotMinTime="07:00:00"
            slotMaxTime="19:00:00"
            allDaySlot={false}
            nowIndicator={true}
          />
        </main>
      </div>

      {/* Intervention Modal */}
      {isModalOpen && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
          <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 700, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                    <CalendarIcon size={20} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{modalMode === 'edit' ? 'Modifier l\'intervention' : 'Nouvelle planification'}</h3>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>{formData.id || 'Nouveau Brouillon'}</p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} /></button>
              </div>

              <div style={{ padding: '25px 30px', display: 'grid', gap: 20, overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Type de planification</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" onClick={() => setFormData(p => ({ ...p, type: 'mission' }))} style={{ flex: 1, padding: '12px', borderRadius: 12, border: formData.type === 'mission' ? '2px solid #3b82f6' : '1px solid var(--color-light-gray)', background: formData.type === 'mission' ? 'rgba(59, 130, 246, 0.1)' : 'var(--color-surface)', color: formData.type === 'mission' ? '#3b82f6' : 'var(--color-text)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s' }}>
                        <Briefcase size={16} /> Mission
                      </button>
                      <button type="button" onClick={() => setFormData(p => ({ ...p, type: 'rdv' }))} style={{ flex: 1, padding: '12px', borderRadius: 12, border: formData.type === 'rdv' ? '2px solid #a855f7' : '1px solid var(--color-light-gray)', background: formData.type === 'rdv' ? 'rgba(168, 85, 247, 0.1)' : 'var(--color-surface)', color: formData.type === 'rdv' ? '#a855f7' : 'var(--color-text)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s' }}>
                        <CalendarIcon size={16} /> RDV
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Statut</label>
                    <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-light-background)', color: 'var(--color-text)' }}>
                      <option value="planned">📅 Planifié</option>
                      <option value="in_progress">🏗️ En cours</option>
                      <option value="completed">✅ Terminé</option>
                      <option value="cancelled">❌ Annulé</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>{formData.type === 'mission' ? 'Chantier associé' : 'Objet du RDV'}</label>
                  {formData.type === 'mission' ? (
                    <select value={formData.project_id} onChange={e => setFormData(p => ({ ...p, project_id: e.target.value }))} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }}>
                      <option value="">Sélectionner un chantier...</option>
                      {rawChantiers?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} placeholder="Ex: Devis chez M. Martin" />
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Adresse & Destination</label>
                  <AddressAutocomplete
                    value={formData.address || selectedChantier?.address || ''}
                    onChange={(val) => setFormData(p => ({ ...p, address: val }))}
                    placeholder="Adresse de l'intervention..."
                  />
                  {settings?.base_coords && (formData.address || selectedChantier?.address) && (
                    <div style={{ marginTop: 10 }}>
                      <TravelIndicator
                        origin={settings.base_coords}
                        destination={formData.address || selectedChantier?.address}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Période de l'intervention</label>
                  <RangeDatePicker
                    startDate={formData.start}
                    endDate={formData.end}
                    onChange={(s, e) => setFormData(p => ({ ...p, start: s, end: e }))}
                    businessHours={businessHours}
                    closures={closures}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 12 }}>Équipe & Intervenants</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                    {allUsers?.filter(u => u.role === 'worker' || u.role === 'foreman' || u.role === 'employee').map(u => (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: formData.assigned_staff.includes(u.id) ? 'var(--color-primary-light)' : 'var(--color-light-background)', border: formData.assigned_staff.includes(u.id) ? '1.5px solid var(--color-primary)' : '1.5px solid var(--color-light-gray)', borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s' }}>
                        <input type="checkbox" checked={formData.assigned_staff.includes(u.id)} onChange={() => setFormData(p => ({ ...p, assigned_staff: p.assigned_staff.includes(u.id) ? p.assigned_staff.filter(x => x !== u.id) : [...p.assigned_staff, u.id] }))} style={{ display: 'none' }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: getWorkerColor(u) }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: formData.assigned_staff.includes(u.id) ? 'var(--color-primary)' : 'var(--color-text)' }}>{u.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Notes & Instructions</label>
                  <textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', padding: '15px', borderRadius: 14, border: '1.5px solid var(--color-light-gray)', minHeight: 100, fontWeight: 500, fontSize: 14, outline: 'none', background: 'var(--color-light-background)', color: 'var(--color-text)' }} placeholder="Détails pour l'équipe (accès, codes, matériel spécifique...)" />
                </div>
              </div>

              <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                {modalMode === 'edit' ? (
                  <button type="button" onClick={handleDelete} style={{ color: '#ef4444', background: '#fef2f2', border: '1px solid #fee2e2', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, fontSize: 13 }}>
                    <Trash2 size={16} /> Supprimer
                  </button>
                ) : <div />}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Annuler</button>
                  <button type="submit" style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                    <Save size={18} /> {modalMode === 'edit' ? 'Mettre à jour' : 'Enregistrer le planning'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm.open && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 450, borderRadius: 24, padding: 30, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, borderRadius: 20, background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Trash2 size={28} />
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: 900, color: 'var(--color-text)' }}>
              {deleteConfirm.type === 'chantier' ? 'Supprimer le chantier ?' : 'Supprimer l\'intervention ?'}
            </h3>
            <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: 30 }}>
              Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm.name}</strong> ?<br/>
              {deleteConfirm.type === 'chantier' 
                ? "Cette action supprimera également toutes les interventions et données liées. Elle est irréversible."
                : "Cette action retirera l'intervention du planning définitivement."}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button 
                onClick={() => setDeleteConfirm({ open: false, id: '', name: '', type: 'chantier' })}
                style={{ padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button 
                onClick={handleConfirmDelete}
                style={{ padding: '12px', borderRadius: 12, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Planning;
